import { useRef, useState, useCallback, useEffect, Fragment } from "react";
import { useMathJax } from "../../../hooks/useMathJax";
import { processText, svgToDataUrl } from "../../../utils/learnUtils";
import { useLearnCtx } from ".././LearnPage";
import { updateProgressProblemsTried } from "../../../actions/progress";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  SIGNAL_START_STOPWATCH,
  STORE_OPEN_PROBLEM_SET,
} from "../../../constants/actionConstants";
import { calcPercentExercisesComplete } from "../../Global/ProblemUtils";
import { useDispatch } from "react-redux";

function stripMathNL(text) {
  if (!text) return text;
  return text
    .replace(/\n+(\$\$|\\\[|\\begin\{)/g, "$1")
    .replace(/(\$\$|\\\]|\\end\{[^}]*\})\n+/g, "$1");
}
const pt = (text) => processText(stripMathNL(text ?? ""));

// '#' marks a fill-in-blank box; skip '#' that is part of a colour (color:#abc)
const splitBlanks = (text) =>
  (text ?? "")
    .replace(/color:#/g, "color:\uE000")
    .split("#")
    .map((part) => part.replace(/color:\uE000/g, "color:#"));
const countBlanks = (text) => splitBlanks(text).length - 1;

export default function ProblemSetComp({ item }) {
  // Get dispatch object
  const dispatch = useDispatch();
  // Get the post id from the URL (needed to send to database)
  const { id } = useParams();
  // Find out if we are editing
  const editing = window.location.href.includes("editcontent");
  // Admins can try problems without their completion being persisted to the database
  const profile = JSON.parse(localStorage.getItem("profile"));
  const isAdmin = !!profile?.result?.admin;
  // Which problem are we showing at the moment?
  const [currentOpenProblem, setCurrentOpenProblem] = useState(-1);
  // Retrieve problems tried from reducer
  const problemsTriedAndAnswers = useSelector(
    (state) => state.ProgressReducer.problemsTried,
  );
  const problemsTried = problemsTriedAndAnswers.map((a) => a.problemId);
  // Get all problem ids
  const problemIds = useSelector((state) => state.ContentReducer.problemIds);
  // Close this problem set if doesn't match global ID (so only one open at the time)
  const globalOpenProblemSet = useSelector(
    (state) => state.AppReducer.openProblemSet,
  );
  const globalOpenProblemIndex = useSelector(
    (state) => state.AppReducer.openProblemIndex,
  );
  const isOpen = globalOpenProblemSet === item._id;
  // Answer to current problem / subproblem
  const [currentAnswer, setCurrentAnswer] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCurrentOpenProblem(globalOpenProblemIndex);
    }
  }, [isOpen, globalOpenProblemIndex]);

  // Find the first problem we haven't worked on (default opens to here)
  const find_first_unsolved_problem = () => {
    for (
      let c_problem = 0;
      c_problem < item.data.problems.length;
      c_problem++
    ) {
      if (!problemsTried.includes(item.data.problems[c_problem]._id)) {
        return c_problem;
      }
    }
    return -1;
  };

  // Open problem set when clicked on
  const toggleProblemSet = () => {
    if (isOpen) {
      dispatch({
        type: STORE_OPEN_PROBLEM_SET,
        payload: { id: null, open_problem: -1 },
      });
      return;
    }
    if (currentOpenProblem === -1) {
      let first_unsolved_problem_index = find_first_unsolved_problem();
      if (first_unsolved_problem_index >= 0) {
        setCurrentOpenProblem(first_unsolved_problem_index);
        dispatch({
          type: STORE_OPEN_PROBLEM_SET,
          payload: { id: item._id, open_problem: first_unsolved_problem_index },
        });
      } else {
        setCurrentOpenProblem(0);
        dispatch({
          type: STORE_OPEN_PROBLEM_SET,
          payload: { id: item._id, open_problem: 0 },
        });
      }
    } else {
      dispatch({
        type: STORE_OPEN_PROBLEM_SET,
        payload: { id: item._id, open_problem: currentOpenProblem },
      });
    }
    dispatch({ type: SIGNAL_START_STOPWATCH, payload: Math.random() });
  };

  // Go to problem
  const gotoProblem = (problem_index) => {
    if (isOpen) {
      setCurrentOpenProblem(problem_index);
    } else {
      dispatch({
        type: STORE_OPEN_PROBLEM_SET,
        payload: { id: item._id, open_problem: problem_index },
      });
    }
  };

  // Stores the answer in database; answerOverride bypasses stale currentAnswer state
  const storeAnswer = (answerOverride) => {
    if (!editing) {
      const answer =
        answerOverride !== undefined ? answerOverride : currentAnswer;
      const problemsTriedAndAnswersCopy = structuredClone(
        problemsTriedAndAnswers,
      );
      const problemPosition = problemsTried.indexOf(
        item.data.problems[currentOpenProblem]._id,
      );
      if (problemPosition !== -1) {
        problemsTriedAndAnswersCopy[problemPosition].answer = answer;
      } else {
        problemsTriedAndAnswersCopy.push({
          problemId: item.data.problems[currentOpenProblem]._id,
          answer: answer,
        });
      }
      dispatch(
        updateProgressProblemsTried(
          id,
          problemsTriedAndAnswersCopy,
          calcPercentExercisesComplete(problemIds, problemsTriedAndAnswersCopy),
          isAdmin,
        ),
      );
    }
  };

  const containerRef = useRef(null);
  const problems = item.data?.problems ?? [];
  const problem = problems[currentOpenProblem];
  const title = item.title ?? item.data?.label ?? "";

  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => {
      containerRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen]);

  const headerTitle = isOpen
    ? `${title} : Exercise ${currentOpenProblem + 1}${problem?.difficulty ? ` (${problem.difficulty})` : ""}`
    : problems.length === 1
      ? `Exercise: ${title}`
      : `Exercises: ${title}`;

  return (
    <div
      id={item?._id}
      ref={containerRef}
      className={`ct__ps ${isOpen ? "ct__ps--open" : ""}`}
    >
      <div className="ct__ps-header">
        <button
          className="ct__ps-toggle"
          onClick={() => toggleProblemSet()}
          aria-expanded={isOpen}
        >
          <span className="ct__ps-icon">{isOpen ? "▾" : "▸"}</span>
          <span className="ct__ps-title">{headerTitle}</span>
        </button>
        <div className="ct__ps-header-dots">
          {problems.map((p, i) => {
            const answered = problemsTried.includes(p._id);
            const active = currentOpenProblem == i;
            return (
              <button
                key={p._id ?? i}
                className={[
                  "ct__ps-nav-dot",
                  active ? "ct__ps-nav-dot--active" : "",
                  answered ? "ct__ps-nav-dot--done" : "",
                ].join(" ")}
                onClick={() => gotoProblem(i)}
                aria-label={`Exercise ${i + 1}${answered ? " (answered)" : ""}`}
                title={p.name ?? `Exercise ${i + 1}`}
              />
            );
          })}
        </div>
      </div>

      {isOpen && problem && (
        <div className="ct__ps-body">
          <ProblemCard
            key={problem._id ?? currentOpenProblem}
            problem={problem}
            idx={currentOpenProblem}
            total={problems.length}
            answered={problemsTried.includes(problem._id)}
            onAnswer={() => storeAnswer()}
            onPrev={() => {
              setCurrentOpenProblem(currentOpenProblem - 1);
            }}
            onNext={() => {
              setCurrentOpenProblem(currentOpenProblem + 1);
            }}
            onClose={() => {
              storeAnswer();
              toggleProblemSet();
            }}
            onAnswerChanged={setCurrentAnswer}
            storeAnswer={storeAnswer}
          />
        </div>
      )}
    </div>
  );
}

function ProblemCard({
  problem,
  idx,
  total,
  answered,
  onAnswer,
  onPrev,
  onNext,
  onClose,
  onAnswerChanged,
  storeAnswer,
}) {
  const bodyRef = useRef(null);
  const ansRef = useRef(null);
  useMathJax(bodyRef, [problem?._id]);
  useMathJax(ansRef, [problem?._id, answered]);

  const { openSupport } = useLearnCtx();
  const handleCardClick = useCallback(
    (e) => {
      const link = e.target.closest("[data-support-id]");
      if (!link) return;
      e.preventDefault();
      openSupport(link.dataset.supportId);
    },
    [openSupport],
  );

  const [entryText, setEntryText] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);

  // Retrieve problems tried from reducer
  const problemsTriedAndAnswers = useSelector(
    (state) => state.ProgressReducer.problemsTried,
  );
  // Set answer from reducer
  useEffect(() => {
    // Stores answer in reducer
    var result = problemsTriedAndAnswers.find(
      (item) => item.problemId === problem._id,
    );
    if (result !== undefined) {
      if (problem.type === "EDCOMP_PROBLEM_MULTICHOICE") {
        setSelectedOption(Number(result.answer));
      } else {
        setEntryText(result.answer);
      }
    }
  }, [problemsTriedAndAnswers, problem._id]);

  const {
    type = "EDCOMP_PROBLEM_TEXT_QA",
    configuration = "standard",
    question,
    question2,
    answer,
    image,
    answer_image,
    image_width,
    answer_image_width,
    answer_type,
    multichoice_options,
  } = problem ?? {};

  // Fill-in-blank boxes ('#') in figure problems; answers stored comma-joined
  const hasBlanks =
    (type === "EDCOMP_PROBLEM_TEXTIMQ_TEXTA" ||
      type === "EDCOMP_PROBLEM_TEXTIMQ_TEXTIMA") &&
    countBlanks(question) + countBlanks(question2) > 0;
  const blankValues = entryText ? String(entryText).split(",") : [];
  const handleBlankChange = (i, value) => {
    const next = [...blankValues];
    while (next.length <= i) next.push("");
    next[i] = value;
    const joined = next.join(",");
    setEntryText(joined);
    onAnswerChanged(joined);
  };
  const blanks = hasBlanks
    ? {
        values: blankValues,
        onChange: handleBlankChange,
        onBlur: () => storeAnswer(),
        width: problem.field_width,
      }
    : null;

  const isMarkComplete = type === "EDCOMP_PROBLEM_NOTEBOOK" || !answer;
  const handleMainBtn = () => {
    onAnswer();
    if (isMarkComplete) {
      if (idx === total - 1) onClose();
      else onNext();
    }
  };

  return (
    <div className="ct__ps-card" ref={bodyRef} onClick={handleCardClick}>
      <div className="ct__ps-question">
        {type === "EDCOMP_PROBLEM_DROPDOWN_TEXT" ? (
          <DropdownTextQuestion
            question={question}
            problem={problem}
            onAnswerChanged={onAnswerChanged}
            onStore={storeAnswer}
          />
        ) : (
          <QuestionBody
            type={type}
            configuration={configuration}
            question={question}
            question2={question2}
            image={image}
            imageWidth={image_width}
            answered={answered}
            answerImage={answer_image}
            blanks={blanks}
          />
        )}
      </div>

      {type !== "EDCOMP_PROBLEM_MULTICHOICE" && !hasBlanks && answer_type === "short" && (
        <div className="ct__ps-entry">
          <input
            className="ct__ps-entry__input"
            type="text"
            value={entryText}
            onChange={(e) => {
              setEntryText(e.target.value);
              onAnswerChanged(e.target.value);
            }}
            onBlur={() => storeAnswer()}
            placeholder="Your answer…"
          />
        </div>
      )}

      {type !== "EDCOMP_PROBLEM_MULTICHOICE" && !hasBlanks && answer_type === "long" && (
        <div className="ct__ps-entry">
          <textarea
            className="ct__ps-entry__textarea"
            rows={4}
            value={entryText}
            onChange={(e) => {
              setEntryText(e.target.value);
              onAnswerChanged(e.target.value);
            }}
            onBlur={() => storeAnswer()}
            placeholder="Your answer…"
          />
        </div>
      )}

      {answered &&
        type !== "EDCOMP_PROBLEM_MULTICHOICE" &&
        type !== "EDCOMP_PROBLEM_NOTEBOOK" &&
        answer !== "" && (
          <div className="ct__ps-answer" ref={ansRef}>
            <AnswerBody
              type={type}
              answer={answer}
              answerImage={
                configuration === "image right a" ||
                configuration === "image left a" ||
                (type === "EDCOMP_PROBLEM_TEXTIMQ_TEXTIMA" &&
                  configuration === "standard" &&
                  image)
                  ? null
                  : answer_image
              }
              answerImageWidth={answer_image_width}
            />
          </div>
        )}

      {type === "EDCOMP_PROBLEM_MULTICHOICE" &&
        multichoice_options?.length > 0 && (
          <MultiChoiceOptions
            options={multichoice_options}
            selected={selectedOption}
            setSelected={setSelectedOption}
            onAnswerChanged={onAnswerChanged}
            onStore={storeAnswer}
            problemId={problem._id}
          />
        )}

      {answered && type === "EDCOMP_PROBLEM_MULTICHOICE" && (
        <div className="ct__ps-answer" ref={ansRef}>
          <AnswerBody
            type={type}
            answer={answer}
            answerImage={answer_image}
            answerImageWidth={answer_image_width}
          />
        </div>
      )}

      <div className="ct__ps-controls">
        <button className="ct__ps-btn" onClick={onPrev} disabled={idx === 0}>
          ← Prev
        </button>
        {!answered && (
          <button
            className="ct__ps-btn ct__ps-btn--answer"
            onClick={handleMainBtn}
          >
            {isMarkComplete ? "Mark as Complete" : "Show Answer"}
          </button>
        )}
        <button
          className="ct__ps-btn"
          onClick={onNext}
          disabled={idx === total - 1}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function MultiChoiceOptions({
  options,
  selected,
  setSelected,
  problemId,
  onAnswerChanged,
  onStore,
}) {
  return (
    <div className="ct__ps-mc">
      {options.map((opt, i) => (
        <label
          key={i}
          className={`ct__ps-mc__option${selected === i ? " ct__ps-mc__option--selected" : ""}`}
        >
          <input
            type="radio"
            name={`mc-${problemId}`}
            value={i}
            checked={selected === i}
            onChange={() => {
              setSelected(i);
              onAnswerChanged(i);
            }}
          />
          <span dangerouslySetInnerHTML={{ __html: pt(opt) }} />
        </label>
      ))}
    </div>
  );
}

/**
 * Renders a fill-in-the-blank question where each '#' in the question text
 * is replaced by an inline dropdown whose options come from multioptions1,
 * multioptions2, … on the problem object.
 */
function DropdownTextQuestion({ question, problem, onAnswerChanged, onStore }) {
  const parts = (question || "").split("#");
  const dropdownCount = parts.length - 1;

  const multioptionsList = Array.from(
    { length: dropdownCount },
    (_, i) => problem[`multioptions${i + 1}`] ?? [],
  );

  const [selections, setSelections] = useState(() =>
    Array(dropdownCount).fill(""),
  );

  // Retrieve problems tried from reducer
  const problemsTriedAndAnswers = useSelector(
    (state) => state.ProgressReducer.problemsTried,
  );
  // Restore saved answer and sync parent currentAnswer
  useEffect(() => {
    var result = problemsTriedAndAnswers.find(
      (item) => item.problemId === problem._id,
    );
    if (result !== undefined) {
      const saved = result.answer.split(",");
      setSelections(saved);
      onAnswerChanged(result.answer);
    }
  }, [problemsTriedAndAnswers, problem._id]);

  const handleChange = (i, value) => {
    const next = [...selections];
    next[i] = value;
    const joined = next.join(",");
    setSelections(next);
    onAnswerChanged(joined);
  };

  return (
    <p className="ct__ps-dropdown-text">
      {parts.map((part, i) => (
        <Fragment key={i}>
          <span dangerouslySetInnerHTML={{ __html: pt(part) }} />
          {i < dropdownCount && (
            <select
              className="ct__ps-dropdown"
              value={selections[i]}
              onChange={(e) => handleChange(i, e.target.value)}
            >
              <option value="">— select —</option>
              {multioptionsList[i].map((opt, j) => (
                <option key={j} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          )}
        </Fragment>
      ))}
    </p>
  );
}

/**
 * Renders text where each '#' becomes an inline input box. `start` is the
 * index of the first box, so boxes in question and question2 share one list.
 */
function BlankText({ text, start = 0, blanks, as: Tag = "p", className }) {
  if (!blanks) {
    return (
      <Tag className={className} dangerouslySetInnerHTML={{ __html: pt(text) }} />
    );
  }
  const parts = splitBlanks(text);
  const w = String(blanks.width ?? "").trim();
  const width = !w ? undefined : /^\d*\.?\d+$/.test(w) ? `${w}em` : w;
  return (
    <Tag className={`ct__ps-blank-text ${className ?? ""}`}>
      {parts.map((part, i) => (
        <Fragment key={i}>
          <span dangerouslySetInnerHTML={{ __html: pt(part) }} />
          {i < parts.length - 1 && (
            <input
              className="ct__ps-blank"
              type="text"
              style={width ? { width } : undefined}
              value={blanks.values[start + i] ?? ""}
              onChange={(e) => blanks.onChange(start + i, e.target.value)}
              onBlur={blanks.onBlur}
            />
          )}
        </Fragment>
      ))}
    </Tag>
  );
}

/**
 * Centred figure on a white background. widthFraction controls max-width
 * (e.g. "0.8" → 80 %; "" or "1" → 100 %).
 */
function CenteredFigure({ src, widthFraction, alt }) {
  const pct = Math.min(1, Math.max(0.05, parseFloat(widthFraction) || 1)) * 100;
  return (
    <div className="ct__ps-fig-wrap" style={{ maxWidth: `${pct}%` }}>
      <img src={src} alt={alt} className="ct__ps-fig" />
    </div>
  );
}

/**
 * Renders the question area. Layout varies by problem type and configuration.
 */
function QuestionBody({
  type,
  configuration,
  question,
  question2,
  image,
  imageWidth,
  answered,
  answerImage,
  blanks,
}) {
  const imgSrc = image ? svgToDataUrl(image) : null;
  const answerImgSrc = answerImage ? svgToDataUrl(answerImage) : null;

  /* ── Types with an embedded figure in the question ── */
  if (
    type === "EDCOMP_PROBLEM_TEXTIMQ_TEXTA" ||
    type === "EDCOMP_PROBLEM_TEXTIMQ_TEXTIMA"
  ) {
    /* Standard: question → centred figure → question2 */
    if (configuration === "standard") {
      const displaySrc = imgSrc
        ? answered && answerImgSrc
          ? answerImgSrc
          : imgSrc
        : null;
      return (
        <>
          {question && <BlankText text={question} blanks={blanks} />}
          {displaySrc && (
            <CenteredFigure
              src={displaySrc}
              widthFraction={imageWidth}
              alt="Problem figure"
            />
          )}
          {question2 && (
            <BlankText
              text={question2}
              start={countBlanks(question)}
              blanks={blanks}
            />
          )}
        </>
      );
    }

    /* Side-by-side: question → [text col | image col] */
    const isRight =
      configuration === "image right" || configuration === "image right a";
    const isLeft =
      configuration === "image left" || configuration === "image left a";
    const isAlt =
      configuration === "image right a" || configuration === "image left a";
    if (isRight || isLeft) {
      const displaySrc =
        isAlt && answered && answerImgSrc ? answerImgSrc : imgSrc;
      const flexVal = parseFloat(imageWidth) || 1;
      const textCol = (
        <BlankText
          as="div"
          className="ct__ps-two-col__text"
          text={question2 || ""}
          start={countBlanks(question)}
          blanks={blanks}
        />
      );
      const imgCol = displaySrc ? (
        <div className="ct__ps-two-col__img" style={{ flex: flexVal }}>
          <div className="ct__ps-fig-wrap">
            <img src={displaySrc} alt="Problem figure" className="ct__ps-fig" />
          </div>
        </div>
      ) : null;
      return (
        <>
          {question && <BlankText text={question} blanks={blanks} />}
          <div className="ct__ps-two-col">
            {isRight ? (
              <>
                {textCol}
                {imgCol}
              </>
            ) : (
              <>
                {imgCol}
                {textCol}
              </>
            )}
          </div>
        </>
      );
    }
  }

  /* EDCOMP_PROBLEM_TEXT_QA and fallback */
  const displayQuestion = question || question2 || "";
  return (
    <>
      {displayQuestion && (
        <p dangerouslySetInnerHTML={{ __html: pt(displayQuestion) }} />
      )}
      {imgSrc && (
        <img src={imgSrc} alt="Problem figure" className="ct__ps-img" />
      )}
    </>
  );
}

/**
 * Renders the answer area. TEXTIMQ_TEXTIMA shows answer_image before the text.
 */
function AnswerBody({ type, answer, answerImage, answerImageWidth }) {
  const cleanAnswer = (answer ?? "")
    .replace(/\\begin\{align\}/g, "\\begin{align*}")
    .replace(/\\end\{align\}/g, "\\end{align*}");
  if (type === "EDCOMP_PROBLEM_TEXTIMQ_TEXTIMA" && answerImage) {
    return (
      <>
        <CenteredFigure
          src={svgToDataUrl(answerImage)}
          widthFraction={answerImageWidth}
          alt="Answer figure"
        />
        {cleanAnswer && (
          <div dangerouslySetInnerHTML={{ __html: pt(cleanAnswer) }} />
        )}
      </>
    );
  }
  return cleanAnswer ? (
    <div dangerouslySetInnerHTML={{ __html: pt(cleanAnswer) }} />
  ) : null;
}
